import React, { useReducer, useEffect, useCallback } from 'react';
import actionCreator from 'utils/actionCreator';
import * as authClient from 'services/authClient';
import { useSnackbar } from 'notistack';
import { serialize } from 'object-to-formdata';
import { getAuthorization } from 'utils/authCookie';
import ErrorCodesFromApi from 'constant/errorCodesFromApi';
import { isUserWithout2FA } from 'utils/userUtils';
import { useNavigate } from 'react-router-dom';

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ||
  '';

const RECAPTCHA_TOKEN_FIELD = 'recaptchaToken';

let recaptchaScriptPromise;

function loadRecaptchaV3Script() {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve(null);
  if (typeof window === 'undefined') return Promise.resolve(null);

  if (window.grecaptcha) return Promise.resolve(window.grecaptcha);

  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('recaptcha-v3');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.grecaptcha));
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.id = 'recaptcha-v3';
    script.async = true;
    script.defer = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
      RECAPTCHA_SITE_KEY,
    )}`;

    script.onload = () => resolve(window.grecaptcha);
    script.onerror = reject;

    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

async function getRecaptchaToken(action) {
  try {
    if (!RECAPTCHA_SITE_KEY) return null;
    if (typeof window === 'undefined') return null;

    await loadRecaptchaV3Script();

    if (!window.grecaptcha || !window.grecaptcha.ready) return null;

    return await new Promise((resolve) => {
      window.grecaptcha.ready(async () => {
        try {
          const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action,
          });
          resolve(token || null);
        } catch (e) {
          resolve(null);
        }
      });
    });
  } catch (e) {
    return null;
  }
}

const AuthContext = React.createContext();

const LOGIN = 'login';
const LOGIN_ERROR = 'login_error';
const LOGIN2FA_ERROR = 'login2fa_error';
const LOGIN_SUCCESS = 'login_success';
const LOGIN2FA_SUCCESS = 'login2fa_success';

const CLEAR_LOGIN_ERROR = 'clear_login_error';

const SELECT_ROLE = 'select_role';
const SELECT_ROLE_ERROR = 'select_role_error';
const SELECT_ROLE_SUCCESS = 'select_role_success';

const SET_NEW_PASSWORD = 'set_new_password';
const SET_NEW_PASSWORD_ERROR = 'set_new_password_error';

const SIGNUP = 'signup';
const IDKIT_SIGNUP = 'idkitSignup';
const SIGNUP_ENTERPRICE = 'signupEnterprice';
const SIGNUP_ENTERPRICE_SUCCESS = 'signupEnterprice_success';
const SIGNUP_ENTERPRICE_ERROR = 'signupEnterprice_error';
const SIGNUP_THIRDPARTY = 'signupThirdparty';
const SIGNUP_THIRDPARTY_SUCCESS = 'signupThirdparty_success';
const SIGNUP_THIRDPARTY_ERROR = 'signupThirdparty_error';
const SIGNUP_ERROR = 'signup_error';
const SIGNUP_SUCCESS = 'signup_success';

const LOGOUT = 'logout';
const LOGOUT_SUCCESS = 'logout_success';

const PAUSE = 'pause';
const PAUSE_ERROR = 'pause_error';
const PAUSE_SUCCESS = 'pause_success';

const CHECK_PIN = 'check_pin';
const CHECK_PIN_ERROR = 'check_pin_error';
const CHECK_PIN_SUCCESS = 'check_pin_success';

const CHECK_IDENTITY_ERROR = 'check_identity_error';
const CHECK_IDENTITY_SUCCESS = 'check_identity_success';
const CHECK_IDENTITY = 'check_identity';

const USER_LOADING = 'USER_LOADING';
const USER_REFRESHED = 'USER_REFRESHED';

const login = actionCreator(LOGIN);
const loginError = actionCreator(LOGIN_ERROR);
const login2faError = actionCreator(LOGIN2FA_ERROR);
const loginSuccess = actionCreator(LOGIN_SUCCESS);
const login2faSuccess = actionCreator(LOGIN2FA_SUCCESS);

const selectRole = actionCreator(SELECT_ROLE);
const selectRoleError = actionCreator(SELECT_ROLE_ERROR);
const selectRoleSuccess = actionCreator(SELECT_ROLE_SUCCESS);

const setNewPassword = actionCreator(SET_NEW_PASSWORD);
const setNewPasswordError = actionCreator(SET_NEW_PASSWORD_ERROR);

const checkPinLoading = actionCreator(CHECK_PIN);
const checkPinError = actionCreator(CHECK_PIN_ERROR);
const checkPinSuccess = actionCreator(CHECK_PIN_SUCCESS);

const pause = actionCreator(PAUSE);
const pauseError = actionCreator(PAUSE_ERROR);
const pauseSuccess = actionCreator(PAUSE_SUCCESS);

const signup = actionCreator(SIGNUP);
const idkitSignup = actionCreator(IDKIT_SIGNUP);
const signupEnterprice = actionCreator(SIGNUP_ENTERPRICE);
const signupEnterpriceSuccess = actionCreator(SIGNUP_ENTERPRICE_SUCCESS);
const signupEnterpriceError = actionCreator(SIGNUP_ENTERPRICE_ERROR);
const signupThirdparty = actionCreator(SIGNUP_THIRDPARTY);
const signupThirdpartySuccess = actionCreator(SIGNUP_THIRDPARTY_SUCCESS);
const signupThirdpartyError = actionCreator(SIGNUP_THIRDPARTY_ERROR);
const signupError = actionCreator(SIGNUP_ERROR);
const signupSuccess = actionCreator(SIGNUP_SUCCESS);

const userLoading = actionCreator(USER_LOADING);
const userRefreshed = actionCreator(USER_REFRESHED);

const logout = actionCreator(LOGOUT);
const logoutSuccess = actionCreator(LOGOUT_SUCCESS);

const checkIdentityError = actionCreator(CHECK_IDENTITY_ERROR);
const checkIdentityLoading = actionCreator(CHECK_IDENTITY);
const checkIdentitySuccess = actionCreator(CHECK_IDENTITY_SUCCESS);

const LOGGED_IN_FIRST_STEP = 'The first step completed successfully.';
const LOGGED_IN = 'Logged In';
const LOGGED_OUT = 'Logged Out';
const SERVER_ERROR =
  'An unexpected Internal Server Error occurred. Please be assured that we are actively investigating the issue.';
const SIGNED_UP = 'Thank you for registering!';
const USER_PAUSED = 'Pause request confirmed. You are now on pause mode.';
const PIN_IS_CORRECT = 'Access granted.';
const RESET_PIN_SUCCESS_MESSAGE =
  "We've sent instructions to reset your PIN to your email.";
const DISABLE_PIN_SUCCESS_MESSAGE =
  "We've sent instructions to disable your PIN to your email.";

const GET_SIGNUP_USER_DETAIL = 'get_signup_user_detail';
const GET_SIGNUP_USER_DETAIL_SUCCESS = 'get_signup_user_detail_success';
const GET_SIGNUP_USER_DETAIL_ERROR = 'get_signup_user_detail_error';
const getSignupUserDetailAction = actionCreator(GET_SIGNUP_USER_DETAIL);
const getSignupUserDetailActionSuccess = actionCreator(
  GET_SIGNUP_USER_DETAIL_SUCCESS,
);
const getSignupUserDetailActionError = actionCreator(
  GET_SIGNUP_USER_DETAIL_ERROR,
);

const UPDATE_SIGNUP_USER_DETAIL = 'update_signup_user_detail';
const UPDATE_SIGNUP_USER_DETAIL_SUCCESS = 'update_signup_user_detail_success';
const UPDATE_SIGNUP_USER_DETAIL_ERROR = 'update_signup_user_detail_error';
const updateSignupUserDetailAction = actionCreator(UPDATE_SIGNUP_USER_DETAIL);
const updateSignupUserDetailActionSuccess = actionCreator(
  UPDATE_SIGNUP_USER_DETAIL_SUCCESS,
);
const updateSignupUserDetailActionError = actionCreator(
  UPDATE_SIGNUP_USER_DETAIL_ERROR,
);

const CHECK_FACIAL_VERIFICATION_STATUS = 'check_facial_verification_status';
const CHECK_FACIAL_VERIFICATION_STATUS_SUCCESS =
  'check_facial_verification_status_success';
const CHECK_FACIAL_VERIFICATION_STATUS_ERROR =
  'check_facial_verification_status_error';
const checkFacialVerificationStatusAction = actionCreator(
  CHECK_FACIAL_VERIFICATION_STATUS,
);
const checkFacialVerificationStatusActionSuccess = actionCreator(
  CHECK_FACIAL_VERIFICATION_STATUS_SUCCESS,
);
const checkFacialVerificationStatusActionError = actionCreator(
  CHECK_FACIAL_VERIFICATION_STATUS_ERROR,
);

const GENERATE_FVS_URL = 'generate_fvs_url';
const GENERATE_FVS_URL_SUCCESS = 'generate_fvs_url_success';
const GENERATE_FVS_URL_ERROR = 'generate_fvs_url_error';
const generateFvsUrlAction = actionCreator(GENERATE_FVS_URL);
const generateFvsUrlActionSuccess = actionCreator(GENERATE_FVS_URL_SUCCESS);
const generateFvsUrlActionError = actionCreator(GENERATE_FVS_URL_ERROR);

const GET_ENTERPRISE_LIST = 'get_enterprise_list';
const GET_ENTERPRISE_LIST_SUCCESS = 'get_enterprise_list_success';
const GET_ENTERPRISE_LIST_ERROR = 'get_enterprise_list_error';
const RESET_PIN_REQUEST = 'reset_pin_request';
const RESET_PIN_REQUEST_SUCCESS = 'reset_pin_request_success';
const RESET_PIN_REQUEST_ERROR = 'reset_pin_request_error';
const INCREMENT_FAILED_ATTEMPTS = 'increment_failed_attempts';
const RESET_FAILED_ATTEMPTS = 'reset_failed_attempts';

const getEnterpriseListAction = actionCreator(GET_ENTERPRISE_LIST);
const getEnterpriseListActionSuccess = actionCreator(
  GET_ENTERPRISE_LIST_SUCCESS,
);
const getEnterpriseListActionError = actionCreator(GET_ENTERPRISE_LIST_ERROR);

const resetPinRequestAction = actionCreator(RESET_PIN_REQUEST);
const resetPinRequestActionSuccess = actionCreator(RESET_PIN_REQUEST_SUCCESS);
const resetPinRequestActionError = actionCreator(RESET_PIN_REQUEST_ERROR);

const initialState = {
  login: {
    error: null,
    loading: false,
    userInfo: null,
    strategy: null,
    initialEmail: null,
  },
  selectRole: {
    error: null,
    loading: false,
    data: null,
  },
  setNewPassword: {
    error: null,
    loading: false,
  },
  signup: {
    error: null,
    loading: false,
  },
  idkitSignup: {
    error: null,
    loading: false,
  },
  signupEnterprice: {
    error: null,
    loading: false,
  },
  signupThirdparty: {
    error: null,
    loading: false,
  },
  pause: {
    error: null,
    loading: false,
  },
  checkPin: {
    error: null,
    loading: false,
  },
  user: null,
  userLoading: true,
  signupData: {
    error: null,
    loading: false,
    data: null,
  },
  updateSignupData: {
    error: null,
    loading: false,
    data: null,
  },
  checkFacialVerificationStatus: {
    error: null,
    loading: false,
    data: null,
  },
  generateFvsUrl: {
    error: null,
    loading: false,
    data: null,
  },
  enterpriseList: {
    error: null,
    loading: false,
    data: null,
  },
  resetPinRequest: {
    error: null,
    loading: false,
    message: null,
  },
  failedAttempts: 0,
};

function authReducer(state, action) {
  const { type, payload } = action;
  switch (type) {
    case LOGIN:
      return {
        ...state,
        login: {
          error: null,
          loading: true,
          userInfo: null,
        },
        user: null,
      };
    case CLEAR_LOGIN_ERROR:
      return {
        ...state,
        login: {
          ...state.login,
          error: null,
        },
      };
    case LOGIN_SUCCESS:
      return {
        ...state,
        login: {
          error: null,
          loading: false,
          userInfo: payload,
        },
        user: null,
        userLoading: false,
      };
    case SELECT_ROLE:
      return {
        ...state,
        selectRole: {
          error: null,
          loading: true,
        },
      };
    case SELECT_ROLE_ERROR:
      return {
        ...state,
        selectRole: {
          error: payload.error,
          loading: false,
        },
      };
    case SELECT_ROLE_SUCCESS:
      return {
        ...state,
        selectRole: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case SET_NEW_PASSWORD:
      return {
        ...state,
        setNewPassword: {
          error: null,
          loading: true,
        },
      };
    case SET_NEW_PASSWORD_ERROR:
      return {
        ...state,
        setNewPassword: {
          error: payload.error,
          loading: false,
        },
      };
    case LOGIN2FA_SUCCESS:
      return {
        ...state,
        login: {
          error: null,
          loading: false,
        },
        user: payload,
        userLoading: false,
        failedAttempts: 0,
      };
    case LOGIN_ERROR:
      return {
        ...state,
        login: {
          error: payload.error,
          loading: false,
          initialEmail: payload.initialEmail,
        },
        user: null,
        userLoading: false,
        failedAttempts: state.failedAttempts + 1,
      };
    case LOGIN2FA_ERROR:
      return {
        ...state,
        login: {
          error: payload.error,
          loading: false,
          userInfo: payload.userInfo,
          strategy: payload.strategy,
        },
        user: null,
        userLoading: false,
        failedAttempts: state.failedAttempts + 1,
      };
    case SIGNUP:
      return {
        ...state,
        signup: {
          error: null,
          loading: true,
        },
      };
    case IDKIT_SIGNUP:
      return {
        ...state,
        idkitSignup: {
          error: null,
          loading: true,
        },
      };
    case SIGNUP_ENTERPRICE:
      return {
        ...state,
        signupEnterprice: {
          error: null,
          loading: true,
        },
      };
    case SIGNUP_ENTERPRICE_SUCCESS:
      return {
        ...state,
        signupEnterprice: {
          error: null,
          loading: false,
        },
      };
    case SIGNUP_ENTERPRICE_ERROR:
      return {
        ...state,
        signupEnterprice: {
          error: payload.error,
          loading: false,
        },
      };
    case SIGNUP_THIRDPARTY:
      return {
        ...state,
        signupThirdparty: {
          error: null,
          loading: true,
        },
      };
    case SIGNUP_THIRDPARTY_SUCCESS:
      return {
        ...state,
        signupThirdparty: {
          error: null,
          loading: false,
        },
      };
    case SIGNUP_THIRDPARTY_ERROR:
      return {
        ...state,
        signupThirdparty: {
          error: payload.error,
          loading: false,
        },
      };
    case SIGNUP_SUCCESS:
      return {
        ...state,
        signup: {
          error: null,
          loading: false,
        },
      };
    case SIGNUP_ERROR:
      return {
        ...state,
        signup: {
          error: payload.error,
          loading: false,
        },
      };
    case LOGOUT:
      return {
        ...state,
        logout: {
          error: payload,
          loading: false,
        },
        user: null,
      };
    case LOGOUT_SUCCESS:
      return {
        ...state,
        logout: {
          error: null,
          loading: false,
        },
        user: null,
        failedAttempts: 0,
      };
    case USER_LOADING:
      return {
        ...state,
        userLoading: true,
      };
    case USER_REFRESHED:
      return {
        ...state,
        user: payload,
      };
    case PAUSE:
      return {
        ...state,
        pause: {
          error: null,
          loading: true,
        },
      };
    case PAUSE_ERROR:
      return {
        ...state,
        pause: {
          error: payload.error,
          loading: false,
        },
      };
    case PAUSE_SUCCESS:
      return {
        ...state,
        pause: {
          error: null,
          loading: false,
        },
        user: payload,
      };

    case CHECK_PIN:
      return {
        ...state,
        checkPin: {
          error: null,
          loading: true,
        },
      };
    case CHECK_PIN_ERROR:
      return {
        ...state,
        checkPin: {
          error: payload.error,
          loading: false,
        },
      };
    case CHECK_PIN_SUCCESS:
      return {
        ...state,
        checkPin: {
          error: null,
          loading: false,
        },
      };
    case CHECK_IDENTITY_ERROR:
      return {
        ...state,
        login: {
          error: payload.error,
          loading: false,
          userInfo: payload.userInfo,
          strategy: payload.strategy,
        },
      };
    case CHECK_IDENTITY_SUCCESS:
      return {
        ...state,
        login: {
          error: null,
          loading: false,
        },
      };
    case CHECK_IDENTITY:
      return {
        ...state,
        login: {
          error: null,
          loading: true,
        },
      };
    case GET_SIGNUP_USER_DETAIL:
      return {
        ...state,
        signupData: {
          error: null,
          loading: true,
          data: null,
        },
      };

    case GET_SIGNUP_USER_DETAIL_SUCCESS:
      return {
        ...state,
        signupData: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case GET_SIGNUP_USER_DETAIL_ERROR:
      return {
        ...state,
        signupData: {
          error: payload.error,
          loading: false,
          data: null,
        },
      };

    case UPDATE_SIGNUP_USER_DETAIL:
      return {
        ...state,
        updateSignupData: {
          error: null,
          loading: true,
        },
      };
    case UPDATE_SIGNUP_USER_DETAIL_SUCCESS:
      return {
        ...state,
        updateSignupData: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case UPDATE_SIGNUP_USER_DETAIL_ERROR:
      return {
        ...state,
        updateSignupData: {
          error: payload.error,
          loading: false,
        },
      };
    case CHECK_FACIAL_VERIFICATION_STATUS:
      return {
        ...state,
        checkFacialVerificationStatus: {
          error: null,
          loading: true,
        },
      };
    case CHECK_FACIAL_VERIFICATION_STATUS_SUCCESS:
      return {
        ...state,
        checkFacialVerificationStatus: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case CHECK_FACIAL_VERIFICATION_STATUS_ERROR:
      return {
        ...state,
        checkFacialVerificationStatus: {
          error: payload.error,
          loading: false,
        },
      };
    case GENERATE_FVS_URL:
      return {
        ...state,
        generateFvsUrl: {
          error: null,
          loading: true,
        },
      };
    case GENERATE_FVS_URL_SUCCESS:
      return {
        ...state,
        generateFvsUrl: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case GENERATE_FVS_URL_ERROR:
      return {
        ...state,
        generateFvsUrl: {
          error: payload.error,
          loading: false,
        },
      };

    case GET_ENTERPRISE_LIST:
      return {
        ...state,
        enterpriseList: {
          error: null,
          loading: true,
          data: null,
        },
      };
    case GET_ENTERPRISE_LIST_SUCCESS:
      return {
        ...state,
        enterpriseList: {
          error: null,
          loading: false,
          data: payload,
        },
      };
    case GET_ENTERPRISE_LIST_ERROR:
      return {
        ...state,
        enterpriseList: {
          error: payload.error,
          loading: false,
        },
      };
    case RESET_PIN_REQUEST:
      return {
        ...state,
        resetPinRequest: {
          error: null,
          loading: true,
          message: null,
        },
      };
    case RESET_PIN_REQUEST_SUCCESS:
      return {
        ...state,
        resetPinRequest: {
          error: null,
          loading: false,
          message: payload,
        },
      };
    case RESET_PIN_REQUEST_ERROR:
      return {
        ...state,
        resetPinRequest: {
          error: payload.error,
          loading: false,
          message: null,
        },
      };

    case INCREMENT_FAILED_ATTEMPTS:
      return {
        ...state,
        failedAttempts: state.failedAttempts + 1,
      };

    case RESET_FAILED_ATTEMPTS:
      return {
        ...state,
        failedAttempts: 0,
      };

    default:
      throw new Error();
  }
}

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getUser() {
    try {
      dispatch(userLoading());
      const user = await authClient.getUser();
      if (Boolean(user?.user2fa)) {
        dispatch(loginSuccess(user));
      } else if (user) {
        dispatch(login2faSuccess(user));
      } else {
        dispatch(loginError({}));
      }
    } catch (e) {
      dispatch(loginError({}));
    }
  }

  // Swaps the user in place: no USER_LOADING, so the app is not replaced by the loader.
  const refreshUser = useCallback(async () => {
    const user = await authClient.fetchCurrentUser();
    if (user) dispatch(userRefreshed(user));
  }, []);

  async function handleLogin(form) {
    try {
      dispatch(login());
      const response = await authClient.login(form);

      const { message, user } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(loginError({ error: message, initialEmail: form.email }));
      } else if (user?.requiresRoleSelection || user?.requiresPasswordChange) {
        enqueueSnackbar(LOGGED_IN_FIRST_STEP, { variant: 'success' });
        dispatch(loginSuccess(user));
      } else if (isUserWithout2FA(user)) {
        enqueueSnackbar(LOGGED_IN, { variant: 'success' });
        const fetchedUser = await authClient.getUser();
        if (!fetchedUser) {
          throw new Error('Failed to fetch user details');
        }
        dispatch(login2faSuccess(fetchedUser));
      } else {
        enqueueSnackbar(LOGGED_IN_FIRST_STEP, { variant: 'success' });
        dispatch(loginSuccess(user));
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(loginError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleSetNewPassword(form) {
    try {
      dispatch(setNewPassword());
      const response = await authClient.setNewPassword(form);

      const { message, user } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(setNewPasswordError({ error: message }));
      } else if (user?.requiresRoleSelection) {
        enqueueSnackbar(LOGGED_IN_FIRST_STEP, { variant: 'success' });
        dispatch(loginSuccess(user));
      } else if (isUserWithout2FA(user)) {
        enqueueSnackbar(LOGGED_IN, { variant: 'success' });
        const fetchedUser = await authClient.getUser();
        if (!fetchedUser) {
          throw new Error('Failed to fetch user details');
        }
        dispatch(login2faSuccess(fetchedUser));
      } else {
        enqueueSnackbar(LOGGED_IN_FIRST_STEP, { variant: 'success' });
        dispatch(loginSuccess(user));
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        setNewPasswordError({
          error: 'Something went wrong! Please try again!',
        }),
      );
    }
  }

  async function handleSelectRole(form) {
    try {
      dispatch(selectRole());
      const response = await authClient.selectRole(form);

      const { message, user } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(selectRoleError({ error: message }));
      } else if (isUserWithout2FA(user)) {
        enqueueSnackbar(LOGGED_IN, { variant: 'success' });
        const fetchedUser = await authClient.getUser();
        if (!fetchedUser) {
          throw new Error('Failed to fetch user details');
        }
        dispatch(login2faSuccess(fetchedUser));
      } else {
        enqueueSnackbar(LOGGED_IN, { variant: 'success' });
        dispatch(selectRoleSuccess(user));
        dispatch(loginSuccess());
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(selectRoleError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleLogin2Fa(form) {
    try {
      dispatch(login());
      const response = await authClient.login2fa(form);

      const { message, code, dataResponse } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        if (code === ErrorCodesFromApi.AUTHENTICATION_ERROR) {
          dispatch(
            login2faError({
              error: message,
              userInfo: dataResponse,
              strategy: form.strategy,
            }),
          );
        } else {
          dispatch(loginError({ error: message }));
        }
      } else {
        enqueueSnackbar(LOGGED_IN, { variant: 'success' });
        const user = await authClient.getUser();
        dispatch(login2faSuccess(user));
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(loginError({ error: 'Invalid code' }));
    }
  }

  async function login2faSendCode(form) {
    try {
      dispatch(login());
      const response = await authClient.login2faSendCode(form);

      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(
          login2faError({ error: message, userInfo: state.login.userInfo }),
        );
      } else {
        enqueueSnackbar('Code has been sent', { variant: 'success' });
        dispatch(loginSuccess(state.login.userInfo));
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(loginError({ error: 'Server error' }));
    }
  }

  async function handleSignup(form) {
    try {
      dispatch(signup());
      const response = await authClient.signup(form);
      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(signupError({ error: message }));
      } else {
        enqueueSnackbar(SIGNED_UP, { variant: 'success' });
        dispatch(signupSuccess());
        window.location = '/thankyou';
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(signupError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleIdkitSignup(form) {
    try {
      dispatch(idkitSignup());
      const response = await authClient.idkitSignup(form);
      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(signupError({ error: message }));
      } else {
        // enqueueSnackbar(SIGNED_UP, { variant: 'success' });
        // dispatch(signupSuccess());
        window.location = '/signup-message';
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(signupError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleSignupEnterprice(form) {
    try {
      dispatch(signupEnterprice());

      const {
        name,
        email,
        abnNumber,
        acnNumber,
        logoImage,
        password,
        rtoNumber,
        firstName,
        lastName,
        phone,
        organizationPhone,
        jobTitle,
        addressLine1,
        suburb,
        state,
        postal,
      } = form;

      const signupForm = {
        contactPerson: {
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          jobTitle: jobTitle,
        },
        address: {
          addressLine1: addressLine1,
          suburb: suburb,
          state: state,
          postal: postal,
        },
        phone: organizationPhone,
        name: name,
        email: email,
        abnNumber: abnNumber,
        rtoNumber: rtoNumber,
        acnNumber: acnNumber,
        password: password,
        logoImage: logoImage,
      };

      const response = await authClient.signupEnterprice(serialize(signupForm));

      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(signupEnterpriceError({ error: message }));
      } else {
        enqueueSnackbar(SIGNED_UP, { variant: 'success' });
        dispatch(signupEnterpriceSuccess());
        window.location = '/thankyou';
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(signupEnterpriceError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleSignupThirdparty(form) {
    try {
      dispatch(signupThirdparty());

      const {
        name,
        email,
        abnNumber,
        acnNumber,
        logoImage,
        password,
        rtoNumber,
        firstName,
        lastName,
        phone,
        organizationPhone,
        jobTitle,
        addressLine1,
        suburb,
        state,
        postal,
      } = form;

      const signupForm = {
        contactPerson: {
          firstName: firstName,
          lastName: lastName,
          phone: phone,
          jobTitle: jobTitle,
        },
        address: {
          addressLine1: addressLine1,
          suburb: suburb,
          state: state,
          postal: postal,
        },
        phone: organizationPhone,
        name: name,
        email: email,
        abnNumber: abnNumber,
        rtoNumber: rtoNumber,
        acnNumber: acnNumber,
        password: password,
        logoImage: logoImage,
      };

      const response = await authClient.signupThirdparty(serialize(signupForm));

      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(signupThirdpartyError({ error: message }));
      } else {
        enqueueSnackbar(SIGNED_UP, { variant: 'success' });
        dispatch(signupThirdpartySuccess());
        window.location = '/thankyou';
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(signupThirdpartyError({ error: 'Invalid Email or Password' }));
    }
  }

  async function handleLogout() {
    dispatch(logout());
    await authClient.logout();
    enqueueSnackbar(LOGGED_OUT, { variant: 'success' });
    dispatch(logoutSuccess());
    navigate('/login');
  }

  async function logoutUnAuthorised() {
    const token = getAuthorization();
    if (token) {
      dispatch(logout());
      await authClient.logout();
    }
    dispatch(logoutSuccess());
  }

  async function pauseUser(form) {
    try {
      dispatch(pause());
      const response = await authClient.pauseUser(form);
      const { message, user } = response.data;
      if (response.status >= 400) {
        dispatch(pauseError({ error: message }));
        enqueueSnackbar(message, { variant: 'error' });
      } else {
        dispatch(pauseSuccess(user));
        enqueueSnackbar(USER_PAUSED, { variant: 'success' });
      }
      return response;
    } catch (e) {
      dispatch(pauseError({ error: SERVER_ERROR }));
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
    }
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function checkPin(form) {
    try {
      dispatch(checkPinLoading());
      const response = await authClient.checkPin(form);
      await sleep(400);
      const { message, user } = response.data;
      if (response.status >= 400) {
        dispatch(checkPinError({ error: message }));
        enqueueSnackbar(message, { variant: 'error' });
      } else {
        dispatch(checkPinSuccess(user));
        enqueueSnackbar(PIN_IS_CORRECT, { variant: 'success' });
      }
      return response;
    } catch (e) {
      dispatch(checkPinError({ error: SERVER_ERROR }));
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
    }
  }

  async function resetPinRequest(form) {
    try {
      dispatch(resetPinRequestAction());
      const response = await authClient.resetPinRequest(form);
      const { message } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        dispatch(resetPinRequestActionError({ error: message }));
      } else {
        enqueueSnackbar(RESET_PIN_SUCCESS_MESSAGE, { variant: 'success' });
        dispatch(resetPinRequestActionSuccess());
      }
      return response;
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(resetPinRequestActionError({ error: SERVER_ERROR }));
    }
  }

  async function disablePinRequest(form) {
    try {
      dispatch(checkIdentityLoading());
      const response = await authClient.disablePinRequest(form);
      const { message, code, dataResponse } = response.data;
      if (response.status >= 400) {
        enqueueSnackbar(message, { variant: 'error' });
        if (code === ErrorCodesFromApi.AUTHENTICATION_ERROR) {
          dispatch(
            checkIdentityError({
              error: message,
              userInfo: dataResponse,
              strategy: form.strategy,
            }),
          );
        } else {
          dispatch(loginError({ error: message }));
        }
      } else {
        enqueueSnackbar(DISABLE_PIN_SUCCESS_MESSAGE, { variant: 'success' });
        dispatch(checkIdentitySuccess());
      }
      return response;
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(loginError({ error: 'Invalid code' }));
    }
  }

  async function getSignupUserDetail(userId) {
    try {
      dispatch(getSignupUserDetailAction());
      const { status, data } = await authClient.getSignupUserDetail(userId);
      if (status >= 400) {
        enqueueSnackbar(data.message, { variant: 'error' });
        dispatch(getSignupUserDetailActionError(data.message));
      } else {
        dispatch(getSignupUserDetailActionSuccess(data));
        return data;
      }
    } catch (e) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        getSignupUserDetailActionError(
          'An error occurred while processing your request.',
        ),
      );
    }
  }

  // `redirect: false` keeps the caller on the page and returns the response, so
  // the signup form can save first and then start facial verification in place.
  // Validation failures carry per-field `errors` rather than a `message`.
  const signupErrorMessage = (data) =>
    data?.message ||
    Object.values(data?.errors ?? {}).join(', ') ||
    'Could not save your details.';

  async function updateSignupData(form, { redirect = true } = {}) {
    try {
      dispatch(updateSignupUserDetailAction());
      const { userId } = form;

      const recaptchaToken = await getRecaptchaToken('updateSignupData');
      const payload = {
        ...form,
        ...(recaptchaToken ? { [RECAPTCHA_TOKEN_FIELD]: recaptchaToken } : {}),
      };

      if (userId) {
        const { status, data } =
          await authClient.updateSignupUserDetail(payload);
        if (status >= 400) {
          enqueueSnackbar(signupErrorMessage(data), { variant: 'error' });
          dispatch(updateSignupUserDetailActionError(signupErrorMessage(data)));
        } else {
          // After updating the signup data, fetch the full user detail so the User Details page can render immediately
          try {
            await getSignupUserDetail(userId);
          } catch (e) {
            // If fetching fails, still navigate; the page will handle the error state
          }
          dispatch(updateSignupUserDetailActionSuccess(data));
          if (!redirect) return data;
          enqueueSnackbar('User detail updated successfully', {
            variant: 'success',
          });
          window.location = '/user-details';
        }
      } else {
        const { status, data } =
          await authClient.updateSignupUserDetail(payload);
        if (status >= 400) {
          enqueueSnackbar(signupErrorMessage(data), { variant: 'error' });
          dispatch(updateSignupUserDetailActionError(signupErrorMessage(data)));
        } else {
          dispatch(updateSignupUserDetailActionSuccess(data));
          if (!redirect) return data;
          enqueueSnackbar('User created successfully', { variant: 'success' });
          window.location = '/thankyou';
        }
      }
    } catch (error) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        updateSignupUserDetailActionError(
          'An error occurred while processing your request.',
        ),
      );
    }
  }

  async function checkFacialStatus(userId) {
    try {
      dispatch(checkFacialVerificationStatusAction());
      const { status, data } = await authClient.checkVerificationStatus(userId);
      if (status >= 400) {
        enqueueSnackbar(data.message, { variant: 'error' });
        dispatch(checkFacialVerificationStatusActionError(data.message));
      } else {
        dispatch(checkFacialVerificationStatusActionSuccess(data));
        return data;
      }
    } catch (error) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        checkFacialVerificationStatusActionError(
          'An error occurred while processing your request.',
        ),
      );
    }
  }

  async function createFVSession(userId) {
    try {
      dispatch(generateFvsUrlAction());
      const { status, data } = await authClient.createFvsSession(userId);
      if (status >= 400) {
        enqueueSnackbar(data.message, { variant: 'error' });
        dispatch(generateFvsUrlActionError(data.message));
      } else {
        dispatch(generateFvsUrlActionSuccess(data));
        return data;
      }
    } catch (error) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        generateFvsUrlActionError(
          'An error occurred while processing your request.',
        ),
      );
    }
  }

  async function getEnterpriseSelectionList(searchText = '') {
    try {
      dispatch(getEnterpriseListAction());
      const { status, data } = await authClient.getEnterpriseList(searchText);
      if (status >= 400) {
        enqueueSnackbar(data.message, { variant: 'error' });
        dispatch(getEnterpriseListActionError(data.message));
      } else {
        dispatch(getEnterpriseListActionSuccess(data));
      }
    } catch (error) {
      enqueueSnackbar(SERVER_ERROR, { variant: 'error' });
      dispatch(
        getEnterpriseListActionError(
          'An error occurred while processing your request.',
        ),
      );
    }
  }

  return (
    <AuthContext.Provider
      value={{
        state,
        login: handleLogin,
        selectRole: handleSelectRole,
        setNewPassword: handleSetNewPassword,
        login2fa: handleLogin2Fa,
        signup: handleSignup,
        idkitSignup: handleIdkitSignup,
        signupEnterprice: handleSignupEnterprice,
        signupThirdparty: handleSignupThirdparty,
        logout: handleLogout,
        logoutUnAuthorised,
        login2faSendCode,
        getUser,
        refreshUser,
        pauseUser,
        checkPin,
        resetPinRequest,
        disablePinRequest,
        getSignupUserDetail,
        updateSignupData,
        checkFacialStatus,
        createFVSession,
        getEnterpriseSelectionList,
        clearLoginError: () => dispatch({ type: CLEAR_LOGIN_ERROR }),
        incrementFailedAttempts: () =>
          dispatch({ type: INCREMENT_FAILED_ATTEMPTS }),
        resetFailedAttempts: () => dispatch({ type: RESET_FAILED_ATTEMPTS }),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

export { AuthContext, AuthProvider, useAuth };
